(() => {
  'use strict';
  const config = window.OudTheme || {};
  const root = (config.root || '/').replace(/\/?$/, '/');
  const $ = (selector, scope = document) => scope.querySelector(selector);
  const $$ = (selector, scope = document) => [...scope.querySelectorAll(selector)];
  const reduced = window.matchMedia('(prefers-reduced-motion: reduce)');
  const motionOff = () => reduced.matches || document.body.dataset.animations === 'false';
  const status = message => { const el = $('#GlobalStatus'); if (el) el.textContent = message; };
  const parse = html => new DOMParser().parseFromString(html, 'text/html');
  let opener = null;
  const lockScroll = () => document.body.classList.toggle('dialog-open', !!$('dialog[open]'));
  function openDialog(dialog, trigger) {
    if (!dialog || !dialog.showModal) return;
    $$('dialog[open]').filter(other => other !== dialog).forEach(other => other.close());
    opener = trigger || document.activeElement;
    dialog.classList.remove('is-closing');
    if (!dialog.open) dialog.showModal();
    $$(`[aria-controls="${dialog.id}"]`).forEach(el => el.setAttribute('aria-expanded', 'true'));
    lockScroll();
    const input = $('input[type="search"]', dialog);
    if (input) input.focus();
  }
  function closeDialog(dialog) {
    if (!dialog || !dialog.open || dialog.classList.contains('is-closing')) return;
    const finish = () => { dialog.close(); dialog.classList.remove('is-closing'); };
    if (motionOff() || !dialog.classList.contains('drawer')) finish();
    else { dialog.classList.add('is-closing'); window.setTimeout(finish, 200); }
  }
  function initDialogs(scope = document) {
    $$('dialog', scope).forEach(dialog => {
      if (dialog.dataset.initialized) return;
      dialog.dataset.initialized = 'true';
      dialog.addEventListener('cancel', event => { event.preventDefault(); closeDialog(dialog); });
      dialog.addEventListener('close', () => {
        $$(`[aria-controls="${dialog.id}"]`).forEach(el => el.setAttribute('aria-expanded', 'false'));
        lockScroll();
        if (opener?.isConnected && !$('dialog[open]')) opener.focus({preventScroll:true});
      });
      dialog.addEventListener('click', event => {
        if (event.target !== dialog) return;
        const bounds = dialog.getBoundingClientRect();
        if (event.clientX < bounds.left || event.clientX > bounds.right || event.clientY < bounds.top || event.clientY > bounds.bottom) closeDialog(dialog);
      });
    });
  }
  let cartQueue = Promise.resolve();
  function queueCart(task) {
    const next = cartQueue.then(task, task);
    cartQueue = next.catch(() => {});
    return next;
  }
  async function jsonRequest(url, options) {
    const response = await fetch(url, {...options, headers:{Accept:'application/json', ...(options?.headers || {})}});
    const data = await response.json();
    if (!response.ok || data.status >= 400) throw new Error(typeof data.description === 'string' ? data.description : data.message || 'Your bag could not be updated. Please try again.');
    return data;
  }
  async function renderCart(sections) {
    let html = sections?.['cart-drawer'];
    if (!html) {
      const response = await fetch(`${root}?sections=cart-drawer`, {headers:{Accept:'application/json'}});
      if (!response.ok) throw new Error('Your bag changed, but could not be displayed. View your bag to continue.');
      const data = await response.json(); html = data['cart-drawer'];
    }
    const incoming = html && $('[data-cart-content]', parse(html));
    const current = $('#CartDrawer [data-cart-content]');
    if (!incoming || !current) throw new Error('Your bag changed, but could not be displayed. View your bag to continue.');
    current.replaceWith(incoming);
    const count = incoming.dataset.itemCount || '0';
    $$('[data-cart-count]').forEach(el => el.textContent = count);
    document.dispatchEvent(new CustomEvent('oud:cart-updated', {detail:{count:Number(count)}}));
    status(`Your bag contains ${count} ${count === '1' ? 'item' : 'items'}.`);
  }
  async function addProduct(form) {
    const button = $('button[type="submit"]', form);
    const error = $('[data-form-error]', form);
    const productSection=form.closest('[data-product-section]');
    const stickyButton=productSection && $('[data-sticky-add]',productSection);
    if (button?.disabled || form.dataset.busy || productSection?.hasAttribute('aria-busy') || form.dataset.variantAvailable === 'false' || form.dataset.canPurchase === 'false') return;
    const quantity = productSection && $('input[name="quantity"]', productSection);
    if (quantity) stepQuantity(quantity, 0);
    if (!form.reportValidity()) return;
    const label = button?.textContent;
    const stickyWasDisabled = stickyButton?.disabled;
    form.dataset.busy = 'true'; form.setAttribute('aria-busy', 'true');
    if (button) { button.disabled = true; button.textContent = button.dataset.loadingLabel || 'Adding…'; }
    if (stickyButton) {stickyButton.disabled=true;stickyButton.setAttribute('aria-busy','true');}
    if (error) error.textContent = '';
    try {
      const body = new FormData(form);
      body.set('sections', 'cart-drawer'); body.set('sections_url', window.location.pathname);
      await queueCart(async () => {
        const data = await jsonRequest(`${root}cart/add.js`, {method:'POST', body});
        await renderCart(data.sections);
        openDialog($('#CartDrawer'), button);
      });
    } catch (err) { if (error) error.textContent = err.message; status(err.message); }
    finally {
      delete form.dataset.busy; form.removeAttribute('aria-busy');
      if (button?.isConnected) { button.disabled = form.dataset.variantAvailable === 'false' || form.dataset.canPurchase === 'false'; button.textContent = button.disabled ? (config.strings?.unavailable || 'Unavailable') : label; }
      if (stickyButton?.isConnected) { stickyButton.disabled = stickyWasDisabled || button?.disabled; stickyButton.removeAttribute('aria-busy'); }
    }
  }
  async function changeCart(key, quantity, trigger) {
    const content = $('#CartDrawer [data-cart-content]');
    const error = $('[data-cart-error]', content || document);
    const focusId = trigger?.id;
    const scrollPosition = $('#CartDrawer .cart-items')?.scrollTop || 0;
    if (error) error.textContent = '';
    content?.setAttribute('aria-busy', 'true');
    try {
      await queueCart(async () => {
        const data = await jsonRequest(`${root}cart/change.js`, {method:'POST', headers:{'Content-Type':'application/json'}, body:JSON.stringify({id:key,quantity,sections:['cart-drawer'],sections_url:window.location.pathname})});
        await renderCart(data.sections);
      });
      const replacement = focusId && document.getElementById(focusId);
      if (replacement) replacement.focus({preventScroll:true});
      else $('#CartDrawer [data-close]')?.focus({preventScroll:true});
      const list = $('#CartDrawer .cart-items'); if (list) list.scrollTop = scrollPosition;
    } catch (err) {
      const liveError = $('#CartDrawer [data-cart-error]'); if (liveError) liveError.textContent = err.message;
      status(err.message);
      // Re-render server state so a rejected quantity does not remain visible.
      try { const response = await fetch(`${root}?sections=cart-drawer`); const data = await response.json(); await renderCart(data); const el = $('#CartDrawer [data-cart-error]'); if (el) el.textContent = err.message; } catch (_) { /* Keep the existing bag and error message. */ }
    } finally { $('#CartDrawer [data-cart-content]')?.removeAttribute('aria-busy'); }
  }
  function stepQuantity(input, direction, allowZero = false) {
    if (!input) return;
    const step = Number(input.step) || 1;
    const min = allowZero ? 0 : Number(input.min) || 1;
    const max = input.hasAttribute('max') ? Number(input.max) : Infinity;
    const limit = Number.isFinite(max) ? min + Math.floor((max - min) / step) * step : Infinity;
    const value = Number(input.value);
    const normalized = min + Math.round(((Number.isFinite(value) ? value : min) - min) / step) * step;
    const next = Math.max(min, Math.min(limit, normalized + direction * step));
    input.value = next;
    input.dispatchEvent(new CustomEvent('oud:quantity-updated', {bubbles:true}));
    return next;
  }
  let variantRequest;
  async function selectOption(link) {
    const current = link.closest('[data-product-section]');
    if (!current || $('[data-product-form][data-busy]', current)) return;
    variantRequest?.abort(); variantRequest = new AbortController();
    const request = variantRequest;
    const url = new URL(link.href);
    url.searchParams.set('section_id', current.dataset.sectionId);
    current.setAttribute('aria-busy','true');
    const quantity = $('input[name="quantity"]', current)?.value;
    $$('button[type="submit"],.sticky-add button',current).forEach(el => el.disabled = true);
    $$('.shopify-payment-button',current).forEach(el=>el.inert=true);
    try {
      const response = await fetch(url, {signal:request.signal});
      if (!response.ok) throw new Error('Option could not be loaded.');
      const incomingDoc = parse(await response.text());
      if (request !== variantRequest || request.signal.aborted) return;
      const incoming = $('[data-product-section]', incomingDoc);
      if (!incoming) throw new Error('Option could not be loaded.');
      // DOMParser enables noscript markup; keep its no-JS styles out of the live page.
      $$('noscript', incoming).forEach(el => el.remove());
      current.replaceWith(incoming);
      const newQuantity = $('input[name="quantity"]', incoming);
      if (newQuantity && quantity) { newQuantity.value = quantity; stepQuantity(newQuantity, 0); }
      $(`[data-option-value="${link.dataset.optionValue}"]`, incoming)?.focus({preventScroll:true});
      const historyUrl = new URL(link.href); const variant = $('input[name="id"]', incoming)?.value;
      if (variant) { historyUrl.searchParams.delete('option_values'); historyUrl.searchParams.set('variant',variant); }
      history.replaceState({},'',historyUrl);
      const oldStructured = $('[data-product-jsonld]'); const newStructured = $('[data-product-jsonld]',incomingDoc);
      if (oldStructured && newStructured) oldStructured.replaceWith(newStructured);
      init(incoming);
      window.OudProductPage?.init(incoming);
      if (window.Shopify?.PaymentButton?.init) window.Shopify.PaymentButton.init();
      if (window.Shopify?.ModelViewerUI) initModels(incoming);
      const recommendations = $('[data-recommendations]');
      if (recommendations && recommendations.dataset.productId !== incoming.dataset.productId) {
        recommendations.dataset.productId=incoming.dataset.productId;
        recommendations.replaceChildren(); recommendations.hidden=true;
        delete recommendations.dataset.loaded;
        loadRecommendations(recommendations,incoming.dataset.productUrl);
      }
      status('Product option updated.');
      document.dispatchEvent(new CustomEvent('oud:product-updated',{detail:{sectionId:incoming.dataset.sectionId,productId:incoming.dataset.productId,variantId:variant}}));
    } catch (err) { if (err.name !== 'AbortError') window.location.assign(link.href); }
    finally { if (request === variantRequest) current.removeAttribute('aria-busy'); }
  }
  let searchTimer, searchRequest;
  function initSearch(scope) {
    $$('[data-predictive-form]',scope).forEach(form => {
      if (form.dataset.initialized) return; form.dataset.initialized='true';
      const input = $('input[type=search]',form), results = $('[data-predictive-results]'), message = $('[data-search-status]');
      input.addEventListener('input', () => {
        clearTimeout(searchTimer); searchRequest?.abort();
        const term=input.value.trim();
        if (!term) { results.innerHTML=''; message.textContent=''; return; }
        message.textContent='Searching...';
        searchTimer=setTimeout(async () => {
          searchRequest=new AbortController();
          const url=new URL(config.searchUrl || `${root}search/suggest`,location.origin);
          url.searchParams.set('q',term);url.searchParams.set('section_id','predictive-search');url.searchParams.set('resources[type]','product,collection,page,article,query');url.searchParams.set('resources[limit]','5');url.searchParams.set('resources[options][unavailable_products]','last');
          try {
            const response=await fetch(url,{signal:searchRequest.signal});
            if (!response.ok) throw new Error('Search unavailable');
            const doc=parse(await response.text());const content=$('.predictive-results',doc);
            results.replaceChildren(...(content ? [content] : []));
            const links=$$('li a',results).length;
            message.textContent=links ? `${links} suggestions. Use Tab or Arrow Down to explore.` : 'No suggestions. Press Enter to search all content.';
          } catch (err) { if (err.name !== 'AbortError') { results.innerHTML='';message.textContent='Press Enter to search all content.'; } }
        },250);
      });
      input.addEventListener('keydown',event=>{if(event.key==='ArrowDown'){const first=$('a',results);if(first){event.preventDefault();first.focus();}}});
      results.addEventListener('keydown',event=>{if(!['ArrowDown','ArrowUp'].includes(event.key))return;event.preventDefault();const links=$$('a',results);const index=links.indexOf(document.activeElement);if(event.key==='ArrowUp'&&index===0)input.focus();else links[(index+(event.key==='ArrowDown'?1:-1)+links.length)%links.length]?.focus();});
    });
  }
  const revealObserver = 'IntersectionObserver' in window ? new IntersectionObserver(entries=>entries.forEach(entry=>{if(entry.isIntersecting){entry.target.classList.add('is-visible');revealObserver.unobserve(entry.target);}}),{threshold:.08}) : null;
  function initReveals(scope) {
    if (document.body.dataset.animations!=='true' || reduced.matches || !revealObserver) return;
    $$('[data-reveal]:not([data-reveal=none]),.product-grid>.product-card',scope).forEach((el,index)=>{
      if(el.closest('[data-reveal=none]'))return;
      if(el.classList.contains('reveal-ready'))return;
      if(!el.dataset.reveal)el.dataset.reveal='up';
      el.style.setProperty('--stagger',`${(index%4)*35}ms`);el.classList.add('reveal-ready');revealObserver.observe(el);
    });
  }
  async function loadRecommendations(el, productUrl) {
    if(el.dataset.loaded || !el.dataset.productId || el.dataset.enabled === 'false' || (el.dataset.source === 'collection' && !productUrl))return;el.dataset.loaded='true';
    const requestedProductId=el.dataset.productId;
    const collectionRefresh=el.dataset.source === 'collection';
    const url=new URL(collectionRefresh ? productUrl : `${root}recommendations/products`,location.origin);
    if(collectionRefresh)url.searchParams.set('section_id',el.dataset.sectionId);
    else ['product_id','section_id','limit','intent'].forEach((name,index)=>url.searchParams.set(name,[el.dataset.productId,el.dataset.sectionId,el.dataset.limit,el.dataset.intent][index]));
    try {
      const response=await fetch(url); if(!response.ok)return;
      const doc=parse(await response.text()); const incoming=$('[data-recommendations]',doc);
      if(!el.isConnected || el.dataset.productId!==requestedProductId)return;
      if(incoming){el.innerHTML=incoming.innerHTML;el.dataset.hasProducts=incoming.dataset.hasProducts;el.hidden=incoming.hidden;init(el);}
    } catch(_){/* Keep the configured collection fallback if recommendations are unavailable. */}
  }
  function initAddresses(scope) {
    $$('[data-country]',scope).forEach(select=>{
      if(select.dataset.initialized)return;select.dataset.initialized='true';
      const province=document.getElementById(select.dataset.provinceId);if(!province)return;
      if(select.dataset.default)select.value=select.dataset.default;
      const update=()=>{
        let values=[];try{values=JSON.parse(select.selectedOptions[0]?.dataset.provinces||'[]');}catch(_){}
        province.replaceChildren(...values.map(([value,label])=>new Option(label,value)));
        if(province.dataset.default)province.value=province.dataset.default;
        province.closest('[data-province-container]').hidden=values.length===0;province.disabled=values.length===0;
      };update();select.addEventListener('change',update);
    });
  }
  function initModels(scope) { $$('model-viewer',scope).forEach(el=>{if(!el.dataset.modelInitialized&&window.Shopify?.ModelViewerUI){new window.Shopify.ModelViewerUI(el);el.dataset.modelInitialized='true';}}); }
  function init(scope=document) {
    initDialogs(scope);initSearch(scope);initReveals(scope);initAddresses(scope);
    $$('.accordion .form-error',scope).forEach(el=>{if(el.textContent.trim())el.closest('.accordion').open=true;});
    $$('.accordion',scope).forEach(details=>{
      if(details.dataset.enhanced)return;details.dataset.enhanced='true';
      const summary=$('summary',details),content=$('.accordion-content',details);
      if(!summary||!content)return;
      summary.addEventListener('click',event=>{
        if(motionOff()||!content.animate)return;
        event.preventDefault();if(details.dataset.animating)return;
        details.dataset.animating='true';const closing=details.open;
        if(!closing)details.open=true;
        const height=content.scrollHeight;
        const animation=content.animate(closing?[{height:`${height}px`,opacity:1},{height:'0px',opacity:0}]:[{height:'0px',opacity:0},{height:`${height}px`,opacity:1}],{duration:180,easing:'ease-out'});
        animation.onfinish=()=>{if(closing)details.open=false;delete details.dataset.animating;};
      });
    });
    $$('[data-sort]',scope).forEach(el=>el.dataset.enhanced='true');
    $$('[data-recommendations]',scope).forEach(el=>loadRecommendations(el));
    if($('model-viewer',scope)&&window.Shopify?.loadFeatures)window.Shopify.loadFeatures([{name:'model-viewer-ui',version:'1.0',onLoad:()=>initModels(scope)}]);
  }
  document.addEventListener('submit',event=>{
    const form=event.target;
    if(form.matches('[data-product-form]')&&$('#CartDrawer')?.showModal){event.preventDefault();addProduct(form);}
    if(form.matches('[data-delete-address]')&&!window.confirm('Delete this address?'))event.preventDefault();
  });
  document.addEventListener('click',event=>{
    const target=event.target.closest('a,button');if(!target)return;
    if(target.matches('[data-open]')){event.preventDefault();openDialog(document.getElementById(target.dataset.open),target);}
    if(target.matches('[data-close]')){event.preventDefault();closeDialog(target.closest('dialog'));}
    if(target.matches('[data-cart-open]')){if($('#CartDrawer')?.showModal){event.preventDefault();openDialog($('#CartDrawer'),target);queueCart(()=>renderCart()).catch(err=>{const el=$('#CartDrawer [data-cart-error]');if(el)el.textContent=err.message;});}}
    if(target.matches('[data-cart-remove]')&&target.closest('#CartDrawer')){event.preventDefault();changeCart(target.dataset.key,0,target);}
    if(target.matches('[data-cart-step]')){const input=$('input',target.closest('.quantity-control'));const quantity=stepQuantity(input,Number(target.dataset.cartStep),true);if(target.closest('#CartDrawer'))changeCart(target.dataset.key,quantity,input);}
    if(target.matches('[data-quantity-step]')){const input=$('input',target.closest('.quantity-control'));stepQuantity(input,Number(target.dataset.quantityStep));}
    if(target.matches('[data-option-value]')&&!event.metaKey&&!event.ctrlKey&&!event.shiftKey&&!event.altKey){event.preventDefault();selectOption(target);}
    if(target.matches('[data-media-select]')){
      event.preventDefault();const gallery=target.closest('.product-gallery');$$('[data-media-id]',gallery).forEach(el=>{const selected=el.dataset.mediaId===target.dataset.mediaSelect;el.hidden=!selected;if(!selected)$$('video',el).forEach(video=>video.pause());});$$('[data-media-select]',gallery).forEach(el=>{if(el===target)el.setAttribute('aria-current','true');else el.removeAttribute('aria-current');});
    }
    if(target.matches('[data-zoom]')){const dialog=$('#ProductZoom');const img=$('[data-zoom-image]',dialog);img.src=target.dataset.zoom;img.alt=target.dataset.alt||'';openDialog(dialog,target);}
    if(target.matches('[data-sticky-add]')){const form=$('[data-product-form]',target.closest('[data-product-section]'));form?.requestSubmit($('button[type=submit]',form));}
    if(target.matches('[data-carousel-prev],[data-carousel-next]')){const carousel=$('.carousel',target.closest('.container'));carousel?.scrollBy({left:(target.hasAttribute('data-carousel-next')?1:-1)*carousel.clientWidth*.8,behavior:motionOff()?'instant':'smooth'});}
    if(target.matches('[data-share]')){
      const message=$('[data-share-status]',target.parentElement);
      (async()=>{try{if(navigator.share)await navigator.share({title:target.dataset.title,url:target.dataset.url});else if(navigator.clipboard){await navigator.clipboard.writeText(target.dataset.url);message.textContent='Link copied.';}else{message.textContent=target.dataset.url;}}catch(err){if(err.name!=='AbortError')message.textContent=target.dataset.url;}})();
    }
  });
  document.addEventListener('change',event=>{
    const target=event.target;
    if(target.matches('input[name="quantity"]') && target.closest('[data-product-section]'))stepQuantity(target,0);
    if(target.matches('[data-cart-quantity]')&&target.closest('#CartDrawer')){const value=Number(target.value);if(Number.isInteger(value)&&value>=0)changeCart(target.dataset.key,value,target);else{target.reportValidity();}}
    if(target.matches('[data-sort]')){const url=new URL(location.href);url.searchParams.set('sort_by',target.value);url.searchParams.delete('page');location.assign(url);}
  });
  document.addEventListener('keydown',event=>{
    if(event.key!=='Tab')return;
    const dialog=$('dialog[open]');if(!dialog)return;
    const focusable=$$('a[href],button:not([disabled]),input:not([disabled]):not([type=hidden]),select:not([disabled]),textarea:not([disabled]),summary,[tabindex]:not([tabindex="-1"])',dialog).filter(el=>el.getClientRects().length&&!el.closest('[inert]'));
    const first=focusable[0],last=focusable.at(-1);
    if(!first){event.preventDefault();dialog.focus();return;}
    if(event.shiftKey&&(document.activeElement===first||!dialog.contains(document.activeElement))){event.preventDefault();last.focus();}
    else if(!event.shiftKey&&(document.activeElement===last||!dialog.contains(document.activeElement))){event.preventDefault();first.focus();}
  });
  let scrollTick=false;
  function updateScroll(){
    scrollTick=false;
    $$('.site-header[data-sticky=true]').forEach(el=>el.classList.toggle('is-scrolled',window.scrollY>40));
    let visible=false;
    $$('[data-product-section]').forEach(section=>{
      const add=$('.add-button',section),sticky=$('.sticky-add',section);
      if(!sticky)return;
      const footer=$('.site-footer');
      const show=!!add && window.innerWidth<768 && add.getBoundingClientRect().bottom<0 && (!footer || footer.getBoundingClientRect().top>window.innerHeight);
      sticky.classList.toggle('is-visible',show);sticky.inert=!show;sticky.setAttribute('aria-hidden',String(!show));visible ||= show;
    });
    document.body.classList.toggle('mobile-sticky-active',visible);
  }
  window.addEventListener('scroll',()=>{if(!scrollTick){scrollTick=true;requestAnimationFrame(updateScroll);}},{passive:true});
  window.addEventListener('resize',updateScroll);
  document.addEventListener('oud:product-updated',updateScroll);
  document.addEventListener('shopify:section:load',event=>{init(event.target);updateScroll();});
  document.addEventListener('shopify:section:select',event=>$$('.reveal-ready',event.target).forEach(el=>el.classList.add('is-visible')));
  document.addEventListener('shopify:block:select',event=>{const accordion=event.target.closest('details');if(accordion)accordion.open=true;});
  document.addEventListener('shopify:section:unload',()=>{$$('dialog[open]').forEach(el=>el.close());lockScroll();});
  window.addEventListener('pageshow',()=>{if($('#CartDrawer'))queueCart(()=>renderCart()).catch(()=>{});});
  init();updateScroll();
})();
